import { useState, useRef, useEffect } from 'react'
import { Routes, Route, NavLink, useNavigate, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useCompany } from '../../hooks/useCompany'
import { useEarlySignalsConfig } from '../../hooks/useEarlySignalsConfig'
import { PLATFORM_LOGO } from '../../utils/constants'
import AdminViewAsSelect from '../../components/layout/AdminViewAsSelect'
import { canSeeSimpleHqNav } from '../simple-hq/constants'
import { companyHasSpendFile } from './briefs/companySources'
import { AIView } from './AIView'
import { DashboardView } from './DashboardView'
import { RiskMatrixView } from './RiskMatrixView'
import { MitigationTrackerView } from './MitigationTrackerView'
import { SpecialReportsView } from './SpecialReportsView'
import { BriefsView } from './briefs/BriefsView'
import { ExecutiveSummaryView } from './ExecutiveSummaryView'
import { SpendView } from './SpendView'
import { SupplierRiskIntakeView } from './SupplierRiskIntakeView'
import { SendSupplierRiskAlertView } from './SendSupplierRiskAlertView'
import { SupplierRiskAlertsInboxView } from './SupplierRiskAlertsInboxView'
import { InterestsModal } from './InterestsModal'
import Footer from '../../components/layout/Footer'
import AdminDashboard from '../../components/modals/AdminDashboard'
import { portfolioQueryForNav } from './esPortfolioFilters'
import './design/early-signals.css'

type NavItem = {
  path: string
  label: string
  /** Shorter desktop label when space is tight; mobile always uses `label`. */
  shortLabel?: string
  icon: string
  tabId: string
  /** Show a small superscript Beta (e.g. feature still in development). */
  beta?: boolean
}

function NavItemLabel({ item, compact }: { item: NavItem; compact?: boolean }) {
  const text = compact ? (item.shortLabel ?? item.label) : item.label
  return (
    <>
      {text}
      {item.beta && (
        <span
          className="ml-1.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide leading-none align-middle border border-current opacity-90"
          aria-hidden="true"
        >
          Beta
        </span>
      )}
    </>
  )
}

const navItems: NavItem[] = [
  { path: 'ai', label: 'Risk analyst', icon: 'fa-robot', tabId: 'ai', beta: true },
  { path: 'special-reports', label: 'Special reports', icon: 'fa-file-pdf', tabId: 'specialReports' },
  { path: 'intake', label: 'Risk intake', icon: 'fa-clipboard-list', tabId: 'supplierRiskIntake' },
  { path: 'briefs', label: 'Briefs', icon: 'fa-book-open', tabId: 'briefs' },
  { path: 'executive-summary', label: 'Risk overview', icon: 'fa-chart-pie', tabId: 'executiveSummary' },
  { path: 'spend', label: 'Spend', icon: 'fa-coins', tabId: 'spend' },
  {
    path: 'dashboard',
    label: 'Risk & mitigation monitor',
    shortLabel: 'Risk monitor',
    icon: 'fa-chart-line',
    tabId: 'dashboard',
  },
  {
    path: 'risk-matrix',
    label: 'Intelligence view',
    shortLabel: 'Intelligence',
    icon: 'fa-table-cells',
    tabId: 'riskMatrix',
  },
  {
    path: 'mitigation',
    label: 'Mitigation tracker',
    shortLabel: 'Mitigation',
    icon: 'fa-list-check',
    tabId: 'mitigation',
  },
  { path: 'risk-alerts', label: 'Risk alerts', icon: 'fa-inbox', tabId: 'riskAlerts' },
  { path: 'send-alert', label: 'Send alert', icon: 'fa-bell', tabId: 'sendRiskAlert' },
]

const PORTFOLIO_NAV_PATHS = new Set(['executive-summary', 'risk-matrix'])

const navPillBase =
  'font-esDisplay text-sm font-semibold rounded-full border px-4 py-2 transition-colors duration-150 whitespace-nowrap shrink-0'

function EarlySignalsNav({ items }: { items: NavItem[] }) {
  // Re-read stored filters when location changes so Overview ↔ Intelligence keep chips in sync.
  useLocation()
  const portfolioQs = portfolioQueryForNav()

  return (
    <nav className="flex flex-wrap items-center gap-2" aria-label="Early Signals">
      {items.map((item) => {
        const qs = PORTFOLIO_NAV_PATHS.has(item.path) ? portfolioQs : ''
        return (
          <NavLink
            key={item.path}
            to={`/early-signals/${item.path}${qs}`}
            end={item.path === 'ai'}
            title={item.beta ? `${item.label} (Beta)` : item.label}
            className={({ isActive }) =>
              `${navPillBase} ${
                isActive
                  ? 'bg-es-signal text-es-white border-es-signal'
                  : 'bg-es-white text-es-deep border-es-hairline hover:border-es-signal hover:bg-es-haze'
              }`
            }
          >
            <NavItemLabel item={item} compact />
          </NavLink>
        )
      })}
    </nav>
  )
}

function ViewAsPreviewBanner({
  companyName,
  onExit,
  elevated,
}: {
  companyName: string
  onExit: () => void
  /** Sit above Admin dashboard (fixed overlay). */
  elevated?: boolean
}) {
  return (
    <div
      className={`border-b border-amber-300 bg-amber-50 text-amber-950 ${
        elevated ? 'fixed top-0 left-0 right-0 z-[1100]' : ''
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">
          Viewing as <span className="font-bold">{companyName}</span>
          <span className="ml-2 text-xs font-medium uppercase tracking-wide text-amber-800">
            Preview
          </span>
        </p>
        <button
          type="button"
          onClick={onExit}
          className="text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full border-2 border-amber-500 bg-white hover:bg-amber-100 transition-colors"
        >
          Exit preview
        </button>
      </div>
    </div>
  )
}

function AccountDropdown({
  user,
  onPreferences,
  onSignOut,
  onNavigateHome,
  onAdmin,
  isAdmin,
  showSimpleHq,
}: {
  user: { email: string } | null
  onPreferences: () => void
  onSignOut: () => void
  onNavigateHome: () => void
  onAdmin: () => void
  isAdmin: boolean
  showSimpleHq: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return (
    <div className="relative shrink-0 hidden md:block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${navPillBase} flex items-center bg-es-white text-es-deep border-es-hairline hover:border-es-signal hover:bg-es-haze`}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Account menu"
      >
        <i className="fa-solid fa-user text-lg" aria-hidden />
        {user && (
          <span className="hidden sm:inline truncate max-w-[120px] ml-1.5">
            {user.email.split('@')[0]}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-64 rounded-[var(--radius-lg)] border border-es-soft bg-es-white shadow-[var(--shadow-float)] py-2 z-[1100]"
          role="menu"
        >
          {isAdmin && (
            <div className="px-4 py-3 border-b border-es-soft" onClick={(e) => e.stopPropagation()}>
              <AdminViewAsSelect variant="early-signals" />
            </div>
          )}
          {showSimpleHq && (
            <NavLink
              to="/simple-hq"
              onClick={() => setOpen(false)}
              className="block w-full text-left px-4 py-2.5 text-sm font-semibold text-teal-800 hover:bg-es-haze"
              role="menuitem"
            >
              Simple HQ
            </NavLink>
          )}
          <button
            type="button"
            onClick={() => {
              onPreferences()
              setOpen(false)
            }}
            className="block w-full text-left px-4 py-2.5 text-sm font-medium text-es-body hover:bg-es-haze"
            role="menuitem"
          >
            Preferences
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                onAdmin()
                setOpen(false)
              }}
              className="block w-full text-left px-4 py-2.5 text-sm font-semibold text-es-signal hover:bg-es-haze"
              role="menuitem"
            >
              <i className="fa-solid fa-shield-halved mr-2" aria-hidden />
              Admin dashboard
            </button>
          )}
          {user && (
            <div className="px-4 py-2 text-xs text-es-muted border-b border-es-soft">
              {user.email}
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              onNavigateHome()
              setOpen(false)
            }}
            className="block w-full text-left px-4 py-2.5 text-sm font-medium text-es-body hover:bg-es-haze"
            role="menuitem"
          >
            Home
          </button>
          {user && (
            <button
              type="button"
              onClick={() => {
                onSignOut()
                setOpen(false)
              }}
              className="block w-full text-left px-4 py-2.5 text-sm font-medium text-es-body hover:bg-es-haze"
              role="menuitem"
            >
              Sign Out
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function EarlySignalsMobileMenu({
  items,
  isOpen,
  onClose,
  onPreferencesClick,
  onAdminClick,
  user,
  isAdmin,
  onSignOut,
  showSimpleHq,
}: {
  items: NavItem[]
  isOpen: boolean
  onClose: () => void
  onPreferencesClick: () => void
  onAdminClick: () => void
  user: { email: string } | null
  isAdmin: boolean
  onSignOut: () => void
  showSimpleHq: boolean
}) {
  if (!isOpen) return null

  return (
    <div className="md:hidden absolute top-full left-0 w-full bg-es-white border-b border-es-hairline shadow-[var(--shadow-float)] z-30">
      <div className="px-4 pt-2 pb-6 space-y-1">
        <NavLink
          to="/?view=landing"
          onClick={onClose}
          className="block px-3 py-3 text-base font-medium text-es-body hover:text-es-navy hover:bg-es-haze rounded-[var(--radius-md)]"
        >
          <i className="fa-solid fa-house mr-2" aria-hidden />
          Home
        </NavLink>
        {showSimpleHq && (
          <NavLink
            to="/simple-hq"
            onClick={onClose}
            className="block px-3 py-3 text-base font-semibold text-teal-800 hover:bg-teal-50 rounded-[var(--radius-md)]"
          >
            Simple HQ
          </NavLink>
        )}
        {items.map((item) => {
          const qs = PORTFOLIO_NAV_PATHS.has(item.path) ? portfolioQueryForNav() : ''
          return (
          <NavLink
            key={item.path}
            to={`/early-signals/${item.path}${qs}`}
            onClick={onClose}
            className={({ isActive }) =>
              `block px-3 py-3 text-base font-medium rounded-[var(--radius-md)] transition-colors ${
                isActive
                  ? 'bg-es-signal text-es-white'
                  : 'text-es-body hover:text-es-navy hover:bg-es-haze'
              }`
            }
          >
            <NavItemLabel item={item} />
          </NavLink>
          )
        })}
        <div className="border-t border-es-soft pt-2 mt-2">
          {isAdmin && (
            <div className="px-3 py-2">
              <AdminViewAsSelect variant="early-signals" />
            </div>
          )}
          <button
            onClick={() => {
              onPreferencesClick()
              onClose()
            }}
            className="block w-full text-left px-3 py-3 text-base font-medium text-es-body hover:bg-es-haze rounded-[var(--radius-md)]"
          >
            Preferences
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                onAdminClick()
                onClose()
              }}
              className="block w-full text-left px-3 py-3 text-base font-semibold text-es-signal hover:bg-es-haze rounded-[var(--radius-md)]"
            >
              <i className="fa-solid fa-shield-halved mr-2"></i>Admin dashboard
            </button>
          )}
          {user && (
            <>
              <div className="px-3 py-2 text-sm text-es-muted">{user.email.split('@')[0]}</div>
              <button
                onClick={() => {
                  onSignOut()
                  onClose()
                }}
                className="block w-full text-left px-3 py-3 text-base font-medium text-es-body hover:bg-es-haze rounded-[var(--radius-md)]"
              >
                Sign Out
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function getCompanyLogoUrl(logoValue: string | undefined): string | null {
  if (!logoValue) return null
  if (logoValue.startsWith('http')) return logoValue
  if (logoValue.startsWith('logo')) return `/assets/${logoValue}`
  return null
}

/** Tenant slug for ES theme overrides (e.g. data-tenant="kenvue"). */
function resolveEsTenantSlug(company?: { name?: string | null; domain?: string | null } | null): string | undefined {
  const domain = company?.domain?.trim().toLowerCase() ?? ''
  const name = company?.name?.trim().toLowerCase() ?? ''
  if (domain === 'kenvue.com' || name === 'kenvue') return 'kenvue'
  return undefined
}

export function EarlySignalsLayout() {
  const navigate = useNavigate()
  const {
    user,
    signOut,
    isAdmin,
    companyId,
    viewAsCompanyId,
    setViewAsCompanyId,
    effectiveCompanyId,
    loading: authLoading,
  } = useAuth()
  const { settings, company, loading: companySettingsLoading } = useCompany()
  const { isTabEnabled } = useEarlySignalsConfig()
  const companyLogoUrl = getCompanyLogoUrl(settings?.theme_config?.logo)
  const tenantSlug = resolveEsTenantSlug(company)
  const showSimpleHq = canSeeSimpleHqNav(user?.email, companyId, viewAsCompanyId)
  const [hasSpendFile, setHasSpendFile] = useState<boolean | null>(null)
  const [interestsModalOpen, setInterestsModalOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [adminDashboardOpen, setAdminDashboardOpen] = useState(false)

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/')
    } catch (e) {
      console.error(e)
    }
  }

  const companyPending = authLoading || (!!user && !effectiveCompanyId)
  const briefsSettingsReady =
    !companyPending &&
    !companySettingsLoading &&
    !!effectiveCompanyId &&
    settings?.company_id === effectiveCompanyId
  const showBriefs = briefsSettingsReady && isTabEnabled('briefs')

  useEffect(() => {
    if (companyPending) return
    if (!isAdmin || !effectiveCompanyId) {
      setHasSpendFile(false)
      return
    }
    let cancel = false
    companyHasSpendFile(effectiveCompanyId)
      .then((hasFile) => {
        if (!cancel) setHasSpendFile(hasFile)
      })
      .catch(() => {
        if (!cancel) setHasSpendFile(false)
      })
    return () => {
      cancel = true
    }
  }, [companyPending, isAdmin, effectiveCompanyId])

  useEffect(() => {
    const handleOpen = () => setAdminDashboardOpen(true)
    const handleClose = () => setAdminDashboardOpen(false)
    window.addEventListener('openAdminDashboard', handleOpen)
    window.addEventListener('closeAdminDashboard', handleClose)
    return () => {
      window.removeEventListener('openAdminDashboard', handleOpen)
      window.removeEventListener('closeAdminDashboard', handleClose)
    }
  }, [])

  const visibleNavItems = navItems.filter((item) => {
    if (item.tabId === 'sendRiskAlert') return isAdmin
    if (item.tabId === 'spend') return isAdmin && hasSpendFile === true
    if (item.tabId === 'briefs') return showBriefs
    return isTabEnabled(item.tabId)
  })
  // Risk alerts inbox is for company context (members + admins viewing as a company)
  const showViewAsBanner = isAdmin && !!viewAsCompanyId
  const previewCompanyName = company?.name?.trim() || 'selected company'
  const previewBanner = showViewAsBanner ? (
    <ViewAsPreviewBanner
      companyName={previewCompanyName}
      onExit={() => setViewAsCompanyId(null)}
      elevated={adminDashboardOpen}
    />
  ) : null

  return (
    <div className="es-app min-h-screen flex flex-col" data-tenant={tenantSlug || undefined}>
      {/* Keep preview visible above Admin dashboard overlay */}
      {adminDashboardOpen && previewBanner}

      {!adminDashboardOpen && (
        <div className="sticky top-0 z-[1100]">
          {previewBanner}
          <header className="relative border-b border-es-hairline bg-es-white/95 backdrop-blur-sm shadow-[var(--shadow-card)]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-start md:items-center min-h-20 py-2 gap-3 w-full min-w-0">
                <div className="flex flex-col items-center justify-center gap-1 shrink-0 md:flex-row md:gap-2 self-center">
                  <NavLink to="/?view=landing" className="flex items-center gap-2 shrink-0">
                    <img
                      src={PLATFORM_LOGO}
                      alt="The Simple"
                      className="h-8 sm:h-9 md:h-10 lg:h-12 w-auto transition-all duration-200"
                    />
                    {companyLogoUrl && (
                      <>
                        <span className="h-6 w-px bg-es-hairline hidden sm:block" aria-hidden />
                        <img
                          src={companyLogoUrl}
                          alt={company?.name || 'Company'}
                          className="h-8 sm:h-9 md:h-10 w-auto max-h-10 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </>
                    )}
                  </NavLink>
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="md:hidden p-2 text-es-muted hover:text-es-navy shrink-0"
                    aria-label="Toggle menu"
                  >
                    <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden />
                  </button>
                </div>
                <div className="flex-1 min-w-0 hidden md:block">
                  <EarlySignalsNav items={visibleNavItems} />
                </div>
                {showSimpleHq && (
                  <NavLink
                    to="/simple-hq"
                    className={`${navPillBase} hidden sm:inline-flex bg-teal-50 text-teal-800 border-teal-600 hover:bg-teal-100 shrink-0 self-center`}
                  >
                    Simple HQ
                  </NavLink>
                )}
                <div className="self-center shrink-0">
                  <AccountDropdown
                    user={user}
                    isAdmin={isAdmin}
                    showSimpleHq={showSimpleHq}
                    onPreferences={() => setInterestsModalOpen(true)}
                    onSignOut={handleSignOut}
                    onNavigateHome={() => navigate('/?view=landing')}
                    onAdmin={() => window.dispatchEvent(new CustomEvent('openAdminDashboard'))}
                  />
                </div>
              </div>
            </div>
            <EarlySignalsMobileMenu
              items={visibleNavItems}
              isOpen={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              onPreferencesClick={() => setInterestsModalOpen(true)}
              onAdminClick={() => window.dispatchEvent(new CustomEvent('openAdminDashboard'))}
              user={user}
              isAdmin={isAdmin}
              showSimpleHq={showSimpleHq}
              onSignOut={handleSignOut}
            />
          </header>
        </div>
      )}

      <InterestsModal isOpen={interestsModalOpen} onClose={() => setInterestsModalOpen(false)} />
      <AdminDashboard />

      {!adminDashboardOpen && (
        <>
          <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
            <Routes>
              <Route index element={<Navigate to="ai" replace />} />
              <Route path="ai" element={<AIView />} />
              <Route
                path="briefs"
                element={
                  companyPending || !briefsSettingsReady ? null : showBriefs ? (
                    <BriefsView />
                  ) : (
                    <Navigate to="../ai" replace />
                  )
                }
              />
              <Route path="executive-summary" element={<ExecutiveSummaryView />} />
              <Route
                path="spend"
                element={
                  companyPending || (isAdmin && hasSpendFile === null) ? null : isAdmin &&
                    hasSpendFile ? (
                    <SpendView />
                  ) : (
                    <Navigate to="../ai" replace />
                  )
                }
              />
              <Route path="dashboard" element={<DashboardView />} />
              <Route path="intelligence" element={<Navigate to="../mitigation" replace />} />
              <Route path="alerts" element={<Navigate to="../mitigation" replace />} />
              <Route path="risk-matrix" element={<RiskMatrixView />} />
              <Route path="mitigation" element={<MitigationTrackerView />} />
              <Route path="category-ai" element={<Navigate to="../ai" replace />} />
              <Route path="special-reports" element={<SpecialReportsView />} />
              <Route path="intake" element={<SupplierRiskIntakeView />} />
              <Route path="risk-alerts" element={<SupplierRiskAlertsInboxView />} />
              <Route path="send-alert" element={<SendSupplierRiskAlertView />} />
            </Routes>
          </main>

          <div
            className="fixed bottom-6 left-6 z-40 flex items-center gap-2 bg-es-navy text-es-cyan px-3 py-1.5 rounded-full shadow-[var(--shadow-card)] text-xs font-esDisplay font-semibold uppercase tracking-wider"
            aria-live="polite"
          >
            <i className="fa-solid fa-circle-info text-es-cyan" aria-hidden />
            System: Active monitoring mode
          </div>

          <Footer />
        </>
      )}
    </div>
  )
}
